/**
 * <ifa-product-cards>
 * Adds a bounded ripple on coarse-pointer activation. Desktop hover motion
 * stays in CSS so reduced-motion and fine pointers do not depend on JS.
 */
if (!customElements.get('ifa-product-cards')) {
  class IfaProductCards extends HTMLElement {
    /** @type {AbortController | null} */
    #abortController = null;

    /** @type {Set<number>} */
    #rippleTimers = new Set();

    connectedCallback() {
      if (this.#abortController) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    }

    disconnectedCallback() {
      this.#rippleTimers.forEach((timerId) => window.clearTimeout(timerId));
      this.#rippleTimers.clear();
      this.querySelectorAll('.ifa-product-cards__ripple').forEach((ripple) => ripple.remove());
      this.#abortController?.abort();
      this.#abortController = null;
    }

    /**
     * @param {PointerEvent} event
     */
    #onPointerDown = (event) => {
      if (event.pointerType === 'mouse') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!(event.target instanceof Element)) return;

      const card = event.target.closest('[data-product-card]');
      if (!(card instanceof HTMLElement) || !this.contains(card)) return;

      const rect = card.getBoundingClientRect();
      const ripple = document.createElement('span');
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;

      ripple.className = 'ifa-product-cards__ripple';
      ripple.setAttribute('aria-hidden', 'true');
      ripple.style.setProperty('--ifa-product-cards-ripple-size', `${size}px`);
      ripple.style.setProperty('--ifa-product-cards-ripple-x', `${x}px`);
      ripple.style.setProperty('--ifa-product-cards-ripple-y', `${y}px`);
      card.append(ripple);

      let timerId = 0;
      const removeRipple = () => {
        ripple.remove();
        this.#rippleTimers.delete(timerId);
      };
      timerId = window.setTimeout(removeRipple, 650);
      this.#rippleTimers.add(timerId);
      ripple.addEventListener('animationend', removeRipple, { once: true });
    };
  }

  customElements.define('ifa-product-cards', IfaProductCards);
}
